import { createElement, useEffect, useRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';

interface DropdownChangeDetail {
	value?: string | number;
	text?: string;
}

interface DropdownChangeEvent {
	detail?: DropdownChangeDetail;
	nativeEvent?: { detail?: DropdownChangeDetail };
	target?: { value?: string | number };
}

interface WeaveDropdownProps
	extends Omit<HTMLAttributes<HTMLElement>, 'onChange' | 'children' | 'placeholder'> {
	placeholder?: string;
	value?: string | number;
	disabled?: boolean;
	modal?: boolean;
	density?: 'high' | 'medium';
	ariaExpanded?: boolean;
	children?: ReactNode;
	onChange?: (detail: { value: string; text?: string }) => void;
}

interface WeaveDropdownOptionProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
	value: string | number;
	disabled?: boolean;
	inert?: boolean;
	children?: ReactNode;
}

type WeaveDropdownDividerProps = HTMLAttributes<HTMLElement>;

export function WeaveDropdown({
	placeholder,
	value,
	disabled,
	modal,
	density = 'high',
	ariaExpanded,
	children,
	onChange,
	...rest
}: WeaveDropdownProps) {
	const selectRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!selectRef.current) return;

		if (value === undefined || value === null || value === '') {
			selectRef.current.removeAttribute('value');
			return;
		}

		selectRef.current.setAttribute('value', String(value));
	}, [value]);

	return createElement(
		'weave-select',
		{
			placeholder,
			disabled,
			modal,
			density,
			'aria-expanded': ariaExpanded,
			ref: (node: HTMLElement | null) => {
				selectRef.current = node;
			},
			onChange: (event: unknown) => {
				const changeEvent = event as DropdownChangeEvent;
				const detail = changeEvent.detail ?? changeEvent.nativeEvent?.detail;
				const selectedValue = detail?.value ?? changeEvent.target?.value ?? '';

				onChange?.({
					value: String(selectedValue),
					text: detail?.text,
				});
			},
			...rest,
		},
		children
	);
}

export function WeaveDropdownOption({
	value,
	disabled = false,
	inert = false,
	children,
	...rest
}: WeaveDropdownOptionProps) {
	const optionRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!optionRef.current) return;
		optionRef.current.setAttribute('value', String(value));
	}, [value]);

	return createElement(
		'weave-select-option',
		{
			disabled,
			inert,
			ref: (node: HTMLElement | null) => {
				optionRef.current = node;
			},
			...rest,
		},
		children
	);
}

export function WeaveDropdownDivider(props: WeaveDropdownDividerProps) {
	return createElement('weave-select-divider', props);
}
